import React from "react";
import {
  Description,
  ArrowForward,
  Refresh,
  VisibilityOutlined
} from "@mui/icons-material";
import { Button, Card, CardContent, Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { CategoryCount } from "../../data/models";
import { ClassificationService } from "../../data/services/classification.service";
import { MainRoutes } from "common/config/route.config";
import "./CategoriesOverview.scss";

interface CategoriesOverviewProps {
  categories?: CategoryCount[];
  totalFiles?: number;
  completedCount?: number;
  queuedCount?: number;
  inProgressCount?: number;
  errorCount?: number;
  classificationName?: string;
  backRoute?: string;
  batchGuid?: string | null;
  onCategoryClick: (category: string, fileCount: number) => void;
  onNewUpload: () => void;
  onRefresh?: () => void;
  onBatchExtractAll?: () => void;
}

const CategoriesOverview: React.FC<CategoriesOverviewProps> = ({
  categories,
  totalFiles,
  completedCount,
  queuedCount,
  inProgressCount,
  errorCount,
  classificationName,
  batchGuid,
  onCategoryClick,
  onRefresh,
  onBatchExtractAll
}) => {
  const navigate = useNavigate();
  const displayCategories = (categories || []).filter(
    (category) => category.name.toLowerCase() !== "uncategorized"
  );

  const actualTotalFiles = completedCount || 0;

  const pendingTotal = (queuedCount || 0) + (inProgressCount || 0);

  const handleRefresh = async () => {
    try {
      // If a refresh callback is provided, call it to refetch data
      if (onRefresh) {
        await onRefresh();
      }
      window.dispatchEvent(
        new CustomEvent("refreshClassifications", {
          detail: { batchGuid }
        })
      );
    } catch (error) {
      console.error("Error refreshing classifications:", error);
    }
  };

  const handleBatchExtract = async () => {
    try {
      // Check if batchGuid is available
      if (!batchGuid) {
        console.error("No batch GUID available for extraction");
        return;
      }

      // Generate sample batch data based on current categories for display
      const sampleBatchData = displayCategories.reduce(
        (acc, category, categoryIndex) => {
          const categoryData: Record<
            string,
            Record<string, string | number>
          > = {};

          // Create sample files for each category
          for (
            let fileIndex = 0;
            fileIndex < Math.min(category.count, 3);
            fileIndex++
          ) {
            const fileData: Record<string, string | number> = {
              "Document Name": `${category.name}_Document_${fileIndex + 1}`,
              Date: new Date().toLocaleDateString(),
              Status: "Extracted",
              Confidence: `${85 + Math.floor(Math.random() * 10)}%`,
              Category: category.name,
              Pages: Math.floor(Math.random() * 10) + 1,
              "File Size": `${(Math.random() * 5 + 1).toFixed(1)} MB`
            };

            // Add category-specific fields
            if (category.name === "Contract") {
              fileData["Contract Number"] = `CNT-${
                Math.floor(Math.random() * 9999) + 1000
              }`;
              fileData["Effective Date"] = new Date(
                2024,
                Math.floor(Math.random() * 12),
                Math.floor(Math.random() * 28) + 1
              ).toLocaleDateString();
              fileData["Expiry Date"] = new Date(
                2025,
                Math.floor(Math.random() * 12),
                Math.floor(Math.random() * 28) + 1
              ).toLocaleDateString();
            } else if (category.name === "Invoice") {
              fileData["Invoice Number"] = `INV-${
                Math.floor(Math.random() * 9999) + 1000
              }`;
              fileData.Amount = `$${(Math.random() * 10000 + 100).toFixed(2)}`;
              fileData["Due Date"] = new Date(
                2024,
                Math.floor(Math.random() * 12),
                Math.floor(Math.random() * 28) + 1
              ).toLocaleDateString();
            }

            categoryData[fileIndex.toString()] = fileData;
          }

          acc[category.name] = categoryData;
          return acc;
        },
        {} as Record<string, Record<string, Record<string, string | number>>>
      );

      const categoryCounts = displayCategories.reduce((acc, category) => {
        acc[category.name] = Math.min(category.count, 3); // Limit to 3 sample files per category
        return acc;
      }, {} as Record<string, number>);

      // Navigate to batch extraction results with the sample data
      navigate(MainRoutes.BATCH_EXTRACTION_RESULTS, {
        state: {
          batchData: sampleBatchData,
          categoryCounts: categoryCounts,
          classificationName: classificationName,
          batchGUID: batchGuid // Use the current batchGuid
        }
      });
    } catch (error) {
      console.error("Error starting batch extraction:", error);
    }
  };

  const handleBatchExtractAll = async () => {
    try {
      // Call the parent callback if provided
      if (onBatchExtractAll) {
        await onBatchExtractAll();
      }

      // Check if batchGuid is available
      if (!batchGuid) {
        console.error("No batch GUID available for extraction");
        return;
      }

      // Call the batch extraction API
      const result = await ClassificationService.batchExtractDocuments([
        batchGuid
      ]);

      if (!result.hasErrors) {
        // Extract the batchGUID from the API response
        let extractedBatchGUID: string | null = null;
        if (
          result.value &&
          Array.isArray(result.value) &&
          result.value.length > 0
        ) {
          extractedBatchGUID =
            result.value[0].batchGuid || result.value[0].batchGUID || null;
        } else if (
          result.value &&
          typeof result.value === "object" &&
          result.value.batchGuid
        ) {
          extractedBatchGUID = result.value.batchGuid || result.value.batchGUID;
        }

        // If we didn't get batchGUID from extraction API, use the original batchGuid
        if (!extractedBatchGUID && batchGuid) {
          extractedBatchGUID = batchGuid;
        }

        // Generate sample batch data based on current categories for display
        // TODO: Replace this with actual extracted data from the API response when available
        const sampleBatchData = displayCategories.reduce(
          (acc, category, categoryIndex) => {
            const categoryData: Record<
              string,
              Record<string, string | number>
            > = {};

            // Create sample files for each category
            for (
              let fileIndex = 0;
              fileIndex < Math.min(category.count, 3);
              fileIndex++
            ) {
              const fileData: Record<string, string | number> = {
                "Document Name": `${category.name}_Document_${fileIndex + 1}`,
                Date: new Date().toLocaleDateString(),
                Status: "Extracted",
                Confidence: `${85 + Math.floor(Math.random() * 10)}%`,
                Category: category.name,
                Pages: Math.floor(Math.random() * 10) + 1,
                "File Size": `${(Math.random() * 5 + 1).toFixed(1)} MB`
              };

              // Add category-specific fields
              if (category.name === "Contract") {
                fileData["Contract Number"] = `CNT-${
                  Math.floor(Math.random() * 9999) + 1000
                }`;
                fileData["Effective Date"] = new Date(
                  2024,
                  Math.floor(Math.random() * 12),
                  Math.floor(Math.random() * 28) + 1
                ).toLocaleDateString();
                fileData["Expiry Date"] = new Date(
                  2025,
                  Math.floor(Math.random() * 12),
                  Math.floor(Math.random() * 28) + 1
                ).toLocaleDateString();
              } else if (category.name === "Invoice") {
                fileData["Invoice Number"] = `INV-${
                  Math.floor(Math.random() * 9999) + 1000
                }`;
                fileData.Amount = `$${(Math.random() * 10000 + 100).toFixed(
                  2
                )}`;
                fileData["Due Date"] = new Date(
                  2024,
                  Math.floor(Math.random() * 12),
                  Math.floor(Math.random() * 28) + 1
                ).toLocaleDateString();
              }

              categoryData[fileIndex.toString()] = fileData;
            }

            acc[category.name] = categoryData;
            return acc;
          },
          {} as Record<string, Record<string, Record<string, string | number>>>
        );

        const categoryCounts = displayCategories.reduce((acc, category) => {
          acc[category.name] = Math.min(category.count, 3); // Limit to 3 sample files per category
          return acc;
        }, {} as Record<string, number>);

        // Navigate to batch extraction results with the data, including the dynamic batchGUID
        navigate(MainRoutes.BATCH_EXTRACTION_RESULTS, {
          state: {
            batchData: sampleBatchData,
            categoryCounts: categoryCounts,
            classificationName: classificationName,
            batchGUID: extractedBatchGUID // Pass the dynamic batchGUID from the API
          }
        });
      } else {
        console.error("Batch extraction failed:", result.errors);
      }
    } catch (error) {
      console.error("Error starting batch extraction:", error);
    }
  };

  const getIconComponent = (categoryName: string) => {
    // Use the same document icon for all categories
    return Description;
  };

  return (
    <>
      <div className="categories-overview">
        <div className="overview-header">
          <div className="header-content">
            <Typography variant="h4" className="header-title">
              {classificationName && classificationName !== "Classification"
                ? `Classification Complete - ${classificationName}`
                : "Classification Complete"}
            </Typography>
            {actualTotalFiles === 0 && (
              <Typography variant="body1" className="header-subtitle">
                Classification is in progress please click on Refresh button to
                update the status.
              </Typography>
            )}
            {actualTotalFiles > 0 && (
              <Typography variant="body1" className="header-subtitle">
                {actualTotalFiles}{" "}
                {actualTotalFiles === 1 ? "document" : "documents"} classified
                into {displayCategories.length}{" "}
                {displayCategories.length === 1 ? "category" : "categories"}
                {pendingTotal > 0 ? (
                  <span>
                    {" "}
                    - {pendingTotal}{" "}
                    {pendingTotal === 1 ? "document" : "documents"} pending for
                    classification
                  </span>
                ) : null}
              </Typography>
            )}
            {errorCount && errorCount > 0 && (
              <Typography variant="body1" className="header-subtitle">
                {errorCount} {errorCount === 1 ? "document" : "documents"}{" "}
                failed to classify
              </Typography>
            )}
          </div>
          <div className="header-actions">
            <Button
              onClick={handleBatchExtract}
              variant="outlined"
              size="large"
              startIcon={<VisibilityOutlined />}
              className="new-upload-button"
            >
              View Batch Extraction
            </Button>
            <Button
              onClick={handleBatchExtractAll}
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              className="new-upload-button"
            >
              Batch Extract All
            </Button>
            <Button
              onClick={handleRefresh}
              variant="outlined"
              size="large"
              startIcon={<Refresh />}
              className="new-upload-button"
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Categories Grid */}
        <div className="categories-grid">
          {displayCategories.map((category) => {
            const IconComponent = getIconComponent(category.name);
            return (
              <Card
                key={category.id}
                className="category-card"
                onClick={() => onCategoryClick(category.name, category.count)}
                sx={{ cursor: "pointer" }}
              >
                <CardContent className="category-card-content">
                  <div className="card-layout">
                    <div className="icon-section">
                      <Box className="category-icon-container">
                        <IconComponent className="category-icon" />
                      </Box>
                    </div>

                    <div className="content-section">
                      <Typography variant="h6" className="category-name">
                        {category.name}
                      </Typography>
                      <Typography variant="body2" className="category-count">
                        {category.count}{" "}
                        {category.count === 1 ? "document" : "documents"}
                      </Typography>
                      {/* <Typography variant="body2" className="category-description">
                        {category.description || `${category.name.toLowerCase()} related documents`}
                      </Typography> */}
                    </div>

                    <div className="arrow-section">
                      <ArrowForward className="arrow-icon" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default CategoriesOverview;
